/**
 * Everything the archive search needs besides its layout: the fused
 * `conversationSearch` query, the hand-off into a real assistant conversation,
 * and the mapping to the shared result rows. The full archive page and the
 * quick-search palette both render from this hook.
 */
import { gql } from "@lytra/frontend-common/__generated__";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import type { ConversationSearchResultItem } from "@lytra/frontend-common";
import { formatDateTimeRelative } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { useCallback, useState } from "react";
import { toast } from "sonner";

const SEARCH_ARCHIVE = gql(`
  query SearchArchive($query: String!, $limit: Int) {
    conversationSearch(query: $query, limit: $limit) {
      score
      snippet
      matchedMessageId
      matchTypes
      conversation {
        id
        lastMessageAt
        title
        aiTitle
        contact {
          id
          emailAddress
          givenName
          familyName
        }
      }
    }
  }
`);

const START_ASSISTANT_CONVERSATION = gql(`
  mutation StartArchiveAssistantConversation($text: String!) {
    assistantConversationStart(text: $text) {
      id
    }
  }
`);

/** The standalone archive view of a conversation, scrolled to the matched message when known. */
export function archiveConversationPath(
  conversationId: string,
  matchedMessageId?: string | null,
): string {
  return matchedMessageId
    ? `/archive/${conversationId}?messageId=${matchedMessageId}`
    : `/archive/${conversationId}`;
}

/** The conversation in the agent route (ticketing view or assistant page, per
 * tenant), scrolled to the matched message when known. */
export function agentConversationPath(
  conversationId: string,
  matchedMessageId?: string | null,
): string {
  return matchedMessageId
    ? `/agent/${conversationId}?messageId=${matchedMessageId}`
    : `/agent/${conversationId}`;
}

export function useArchiveSearch() {
  const navigate = useNavigate();
  const [lastQuery, setLastQuery] = useState("");
  const [runSearch, { data, loading, error, called }] = useLazyQuery(SEARCH_ARCHIVE);
  const [startConversation, { loading: escalating }] = useMutation(
    START_ASSISTANT_CONVERSATION,
    {
      onCompleted: (result) => {
        void navigate(`/agent/general/${result.assistantConversationStart.id}`);
      },
      onError: () => {
        toast.error("Gespräch konnte nicht gestartet werden", {
          description: "Bitte versuchen Sie es erneut.",
        });
      },
    },
  );

  const results: ConversationSearchResultItem[] = (data?.conversationSearch ?? []).map(
    ({ conversation, snippet, matchedMessageId, matchTypes }) => {
      const contact = conversation.contact;
      const contactName = contact
        ? `${contact.givenName ?? ""} ${contact.familyName ?? ""}`.trim() ||
          contact.emailAddress
        : null;
      return {
        conversationId: conversation.id,
        title: conversation.title ?? conversation.aiTitle ?? null,
        subtitle: contactName,
        snippet,
        matchedMessageId,
        dateLabel: formatDateTimeRelative(conversation.lastMessageAt),
        matchTypes,
      };
    },
  );

  const search = useCallback(
    (text: string) => {
      const query = text.trim();
      if (query.length < 2) return;
      setLastQuery(query);
      void runSearch({ variables: { query, limit: 25 } });
    },
    [runSearch],
  );

  /** Starts an assistant conversation from the last searched text. Resolves
   * to whether it started; on success the hook has already navigated to it. */
  const askAssistant = async (): Promise<boolean> => {
    if (!lastQuery) return false;
    const result = await startConversation({ variables: { text: lastQuery } });
    return Boolean(result.data);
  };

  return {
    search,
    results,
    loading,
    error,
    called,
    lastQuery,
    askAssistant,
    escalating,
  };
}
